import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, WritableSignal, computed, input, signal } from '@angular/core';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogService } from 'primeng/dynamicdialog';
import { FileProgressEvent, FileSelectEvent, FileSendEvent, FileUpload, FileUploadErrorEvent, FileUploadEvent, FileUploadModule } from 'primeng/fileupload';
import { ProgressBarModule } from 'primeng/progressbar';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { TripJournalService } from 'src/app/api/trip-journal.service';
import { ConfigService } from 'src/app/config.service';
import { ErrorService } from 'src/app/services/error.service';
import { IconService } from 'src/app/ui/icon.service';
import { ImageService } from 'src/app/utility/image.service';
import { MessageBoxResults } from '../../incidents/modals/message-box/message-box.component';
import { IncidentModalService } from '../../incidents/modals/incident-modal.service';


@Component({
  selector: 'app-group-photo',
  standalone: true,
  imports: [FontAwesomeModule, FileUploadModule, ProgressSpinnerModule, ProgressBarModule, ButtonModule],
  providers: [DialogService],
  templateUrl: './group-photo.component.html',
  styleUrl: './group-photo.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GroupPhotoComponent {

  hasPhoto = input<boolean>(false)
  tripId = input<number>(-1)

  photoUrl = computed(() => {
    return `${this.config.tripJournaPhotoUrl}/group-photos/${this.tripId().toString()}.jpg`
  })

  groupPhotoUploadUrl = computed(() => {

    return `${this.config.dataApiUrl}/trip-journals/trip/${this.tripId()}/group-photo`

  })


  constructor(
    private config: ConfigService,
    public icons: IconService,
    private messageService: MessageService,
    private errorService: ErrorService,
    private tripJournalService: TripJournalService,
    private imageService: ImageService,
    private dialogService: DialogService,
    private modalService: IncidentModalService
  ) { }

  uploadingGroupPhoto: WritableSignal<boolean> = signal(false)
  groupPhotoUploadProgress: WritableSignal<number> = signal(0)
  fileUploaded = computed(() => {
    return this.uploadingGroupPhoto() && this.groupPhotoUploadProgress() === 100
  })


  @ViewChild('uploadComponent')
  uploadComponent?: FileUpload

  @Output() photoUploaded: EventEmitter<void> = new EventEmitter()

  onUpload(event: FileUploadEvent) {
    this.uploadingGroupPhoto.set(false)
    this.groupPhotoUploadProgress.set(0)

    this.photoUploaded.emit()
  }

  onProgress(event: FileProgressEvent) {
    this.groupPhotoUploadProgress.set(event.progress)
  }

  onSend(event: FileSendEvent) {
    this.uploadingGroupPhoto.set(true)
  }

  onUploadError(event: FileUploadErrorEvent) {
    if (this.uploadComponent) {
      this.uploadComponent.files = []
      this.groupPhotoUploadProgress.set(0)
      this.uploadingGroupPhoto.set(false)
    }

    this.errorService.error("There was an error uploading the group photo.", event?.error?.error, this.messageService)
  }

  onSelect(event: FileSelectEvent) {
    const file = event.files[0]

    if (!file || !this.uploadComponent) {
      return
    }

    // Resize BEFORE uploading. With PrimeNG auto-upload the send would race the
    // async resize and ship the original full-size file, which drops on weak
    // connections (busboy "Unexpected end of form"). Wait for the resize, then upload.
    this.imageService.resizeImage(file, 1920, 1080).then((resizedFile) => {
      if (this.uploadComponent) {
        this.uploadComponent.files = [resizedFile]
        this.uploadComponent.upload()
      }
    }).catch((err) => {
      if (this.uploadComponent) {
        this.uploadComponent.files = []
      }
      this.errorService.error("This image couldn't be processed. If it's a HEIC/HEIF photo (the default on iPhones), please convert it to JPEG first, then try again.", err, this.messageService)
    })
  }

  removeGroupPhoto() {
    this.modalService.showMessageBox(this.dialogService, "Delete Group Photo", "Are you sure you want to delete the group photo?", { confirmButtonText: "Yes", cancelButtonText: "Cancel", position: "center" }).onClose.subscribe(result => {
      if (result === MessageBoxResults.Confirmed) {
        this.tripJournalService.removeGroupPhoto(this.tripId()).subscribe({
          next: () => {
            this.photoUploaded.emit()
          },
          error: (err) => {
            this.errorService.error("There was a problem removing the group photo", err, this.messageService)
          }
        })
      }
    })
   

  }

}
